import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, ArrowLeft, Phone, Mail, Building2, Inbox, Lock, LogOut, FileText, Download, Plus, Trash2, X, Send, Package, FileSpreadsheet } from "lucide-react";
import { Logo, Eyebrow } from "@/components/Reveal";
import ThemeToggle from "@/components/ThemeToggle";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { FAMILIES } from "@/lib/families";
import { siteImageUrl, refreshSiteAssets } from "@/lib/siteAssets";

const fmt = (iso) =>
    new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const TOKEN_KEY = "roxy_admin_token";
const getToken = () => sessionStorage.getItem(TOKEN_KEY);
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

const inputCls =
    "w-full bg-paper border border-line rounded-md px-4 py-3 text-sm text-ink focus:outline-none focus:border-copper transition-colors duration-300";
const cellCls =
    "w-full bg-paper border border-line rounded-md px-2 py-2 text-xs text-ink focus:outline-none focus:border-copper";

const errText = (e) => {
    const d = e.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
    return "Something went wrong — please try again.";
};

const EMPTY_ITEM = { description: "", size: "", quantity: "", unit: "mtrs", rate: "" };
const STATUS_STYLES = {
    draft: "border-line text-mist",
    sent: "border-copper/40 bg-copper-light text-copper",
    accepted: "border-emerald-300 bg-emerald-50 text-emerald-700",
    rejected: "border-red-300 bg-red-50 text-red-700",
};

const STAGES = ["new", "contacted", "qualified", "quotation_pending", "quoted", "follow_up", "negotiation", "won", "lost"];
const STAGE_LABEL = { new: "New", contacted: "Contacted", qualified: "Qualified", quotation_pending: "Quote Pending", quoted: "Quoted", follow_up: "Follow-up", negotiation: "Negotiation", won: "Won", lost: "Lost" };
const STAGE_STYLES = {
    new: "border-copper/40 bg-copper-light text-copper",
    contacted: "border-blue-300 bg-blue-50 text-blue-700",
    qualified: "border-indigo-300 bg-indigo-50 text-indigo-700",
    quotation_pending: "border-amber-300 bg-amber-50 text-amber-700",
    quoted: "border-copper/40 bg-copper-light text-copper",
    follow_up: "border-amber-300 bg-amber-50 text-amber-700",
    negotiation: "border-violet-300 bg-violet-50 text-violet-700",
    won: "border-emerald-300 bg-emerald-50 text-emerald-700",
    lost: "border-red-300 bg-red-50 text-red-700",
};

function QuoteBuilder({ enquiry, onClose, onSaved }) {
    const [customer, setCustomer] = useState({
        name: enquiry?.name || "", company: enquiry?.company || "",
        phone: enquiry?.phone || "", email: enquiry?.email || "",
    });
    const [items, setItems] = useState([{ ...EMPTY_ITEM, description: enquiry?.interest !== "General" ? enquiry?.interest || "" : "" }]);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
    const subtotal = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0), 0);
    const gst = subtotal * 0.18;

    const save = async (e) => {
        e.preventDefault();
        const clean = items.filter((it) => it.description.trim());
        if (!clean.length) return toast.error("Add at least one item");
        setSaving(true);
        try {
            const res = await api.post("/quotes", {
                enquiry_id: enquiry?.id || null,
                customer,
                items: clean.map((it) => ({ ...it, quantity: parseFloat(it.quantity) || 0, rate: parseFloat(it.rate) || 0 })),
                notes,
            }, { headers: authHeader() });
            toast.success(`Quotation ${res.data.quote_id} created`, { description: `Total ${inr(res.data.total)} incl. GST` });
            onSaved();
            onClose();
        } catch (err) {
            toast.error(errText(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-inkdark/60 flex items-start justify-center overflow-y-auto py-8 px-4" onClick={onClose}>
            <form
                data-testid="quote-builder"
                onSubmit={save}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-3xl bg-white rounded-md border border-line shadow-2xl p-6 md:p-8"
            >
                <div className="flex items-center justify-between mb-6">
                    <p className="font-display font-semibold text-2xl text-ink">New <span className="text-copper">quotation</span></p>
                    <button type="button" data-testid="quote-builder-close" onClick={onClose} className="w-9 h-9 rounded-full bg-paper border border-line flex items-center justify-center hover:border-copper" aria-label="Close">
                        <X size={15} />
                    </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    <input data-testid="quote-customer-name" required value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="Customer name *" className={cellCls + " !py-2.5 !text-sm"} />
                    <input data-testid="quote-customer-company" value={customer.company} onChange={(e) => setCustomer({ ...customer, company: e.target.value })} placeholder="Company" className={cellCls + " !py-2.5 !text-sm"} />
                    <input data-testid="quote-customer-phone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} placeholder="Phone" className={cellCls + " !py-2.5 !text-sm"} />
                    <input data-testid="quote-customer-email" type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} placeholder="Email (links quote to customer portal)" className={cellCls + " !py-2.5 !text-sm"} />
                </div>

                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">Line items</p>
                <div className="rounded-md border border-line overflow-x-auto mb-3">
                    <table className="w-full min-w-[680px] text-sm">
                        <thead>
                            <tr className="border-b border-line bg-paper2">
                                {["Description", "Size", "Qty", "Unit", "Rate (₹)", "Amount", ""].map((h) => (
                                    <th key={h} className="text-left font-mono text-[9px] uppercase tracking-[0.15em] text-dim px-2 py-2.5">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it, i) => (
                                <tr key={i} className="border-b border-line last:border-b-0" data-testid={`quote-item-row-${i}`}>
                                    <td className="px-1.5 py-1.5"><input data-testid={`quote-item-${i}-description`} required value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className={cellCls} placeholder="e.g. XLPE Armoured LT 3.5C" /></td>
                                    <td className="px-1.5 py-1.5 w-24"><input data-testid={`quote-item-${i}-size`} value={it.size} onChange={(e) => setItem(i, "size", e.target.value)} className={cellCls} placeholder="95 sqmm" /></td>
                                    <td className="px-1.5 py-1.5 w-20"><input data-testid={`quote-item-${i}-quantity`} type="number" min="0" step="any" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className={cellCls} placeholder="0" /></td>
                                    <td className="px-1.5 py-1.5 w-20"><input data-testid={`quote-item-${i}-unit`} value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className={cellCls} /></td>
                                    <td className="px-1.5 py-1.5 w-24"><input data-testid={`quote-item-${i}-rate`} type="number" min="0" step="any" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} className={cellCls} placeholder="0.00" /></td>
                                    <td className="px-2 py-1.5 w-24 font-mono text-xs text-ink text-right" data-testid={`quote-item-${i}-amount`}>
                                        {inr((parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0))}
                                    </td>
                                    <td className="px-1.5 py-1.5 w-9 text-center">
                                        <button type="button" data-testid={`quote-item-delete-${i}`} onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} className="text-dim hover:text-red-600" aria-label="Delete row"><Trash2 size={13} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <button type="button" data-testid="quote-add-item" onClick={() => setItems((p) => [...p, { ...EMPTY_ITEM }])} className="mb-5 rounded-full border border-line text-mist font-mono text-[10px] uppercase tracking-[0.18em] px-4 py-2 hover:border-copper hover:text-copper transition-colors flex items-center gap-2">
                    <Plus size={12} /> Add item
                </button>

                <textarea data-testid="quote-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the customer (optional)" rows={2} className={inputCls + " mb-5 !text-xs"} />

                <div className="rounded-md bg-paper2 border border-line p-4 mb-6 font-mono text-xs space-y-1.5" data-testid="quote-totals">
                    <p className="flex justify-between text-mist"><span>SUBTOTAL</span><span data-testid="quote-subtotal">{inr(subtotal)}</span></p>
                    <p className="flex justify-between text-mist"><span>GST @ 18%</span><span data-testid="quote-gst">{inr(gst)}</span></p>
                    <p className="flex justify-between text-ink text-sm font-bold pt-1.5 border-t border-line"><span>GRAND TOTAL</span><span className="text-copper" data-testid="quote-total">{inr(subtotal + gst)}</span></p>
                </div>

                <button data-testid="quote-save-btn" disabled={saving} className="w-full rounded-full bg-copper text-white font-mono uppercase tracking-[0.18em] text-[11px] py-4 hover:bg-copper-bright transition-colors disabled:opacity-50">
                    {saving ? "Creating…" : "Create Quotation"}
                </button>
            </form>
        </div>
    );
}

function QuotesTab({ quotes, onStatus, onPdf, onEmail, onDelete, emailing }) {
    if (quotes.length === 0) {
        return (
            <div className="border border-line bg-white rounded-md p-12 text-center" data-testid="quotes-empty">
                <FileText size={28} className="text-copper mx-auto mb-4" />
                <p className="font-display font-semibold text-2xl text-ink mb-2">No quotations yet</p>
                <p className="text-mist text-sm">Open an enquiry and hit “Quote” to price it.</p>
            </div>
        );
    }
    return (
        <div className="border border-line bg-white rounded-md divide-y divide-line overflow-hidden" data-testid="quotes-list">
            {quotes.map((q) => (
                <div key={q.quote_id} className="p-5 md:p-6" data-testid={`quote-row-${q.quote_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-3">
                            <span className="font-mono text-xs text-ink font-semibold">{q.quote_id}</span>
                            <span className={`font-mono text-[10px] tracking-[0.15em] uppercase border px-2 py-1 rounded-full ${STATUS_STYLES[q.status] || STATUS_STYLES.draft}`} data-testid={`quote-status-${q.quote_id}`}>
                                {q.status}
                            </span>
                        </div>
                        <span className="font-mono text-[10px] text-dim uppercase">{fmt(q.created_at)}</span>
                    </div>
                    <p className="font-display font-semibold text-lg text-ink">
                        {q.customer?.name}{q.customer?.company ? ` · ${q.customer.company}` : ""}
                        <span className="text-mist text-sm font-body font-normal ml-3">{q.items?.length || 0} item{(q.items?.length || 0) !== 1 ? "s" : ""}</span>
                    </p>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3">
                        <span className="font-mono text-sm text-copper font-semibold" data-testid={`quote-total-${q.quote_id}`}>{inr(q.total)} <span className="text-dim text-[10px] font-normal">incl. GST</span></span>
                        <select
                            data-testid={`quote-status-select-${q.quote_id}`}
                            value={q.status}
                            onChange={(e) => onStatus(q.quote_id, e.target.value)}
                            className="bg-paper border border-line rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-ink focus:outline-none focus:border-copper cursor-pointer"
                        >
                            {["draft", "sent", "accepted", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button
                            data-testid={`quote-pdf-${q.quote_id}`}
                            onClick={() => onPdf(q.quote_id)}
                            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-mist hover:text-copper transition-colors"
                        >
                            <Download size={13} /> PDF
                        </button>
                        {q.customer?.email ? (
                            <button
                                data-testid={`quote-email-${q.quote_id}`}
                                onClick={() => onEmail(q.quote_id)}
                                disabled={emailing === q.quote_id}
                                className="flex items-center gap-2 rounded-full bg-ink text-white font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-1.5 hover:bg-copper transition-colors disabled:opacity-50"
                            >
                                <Send size={11} /> {emailing === q.quote_id ? "Sending…" : q.emailed_at ? "Resend Email" : "Email Quote"}
                            </button>
                        ) : (
                            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-dim" title="No customer email on this quotation">No email on record</span>
                        )}
                        {q.emailed_at && (
                            <span className="font-mono text-[9px] text-emerald-700" data-testid={`quote-emailed-${q.quote_id}`}>
                                Emailed to {q.emailed_to} · {fmt(q.emailed_at)}
                            </span>
                        )}
                        <button
                            data-testid={`quote-delete-${q.quote_id}`}
                            onClick={() => onDelete(q.quote_id)}
                            aria-label={`Delete ${q.quote_id}`}
                            className="ml-auto flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-dim hover:text-red-600 transition-colors"
                        >
                            <Trash2 size={12} /> Delete
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

function InventoryTab({ drums, products, onAdd, onUpdate, onDelete }) {
    const [form, setForm] = useState({ sku: "", brand: "", original_length: "", location: "Chickpet Warehouse", status: "sealed" });
    const [adding, setAdding] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        setAdding(true);
        onAdd({ ...form, original_length: parseFloat(form.original_length) || 0 }, () => {
            setForm({ sku: "", brand: "", original_length: "", location: "Chickpet Warehouse", status: "sealed" });
            setAdding(false);
        });
    };

    return (
        <div>
            <form onSubmit={submit} className="rounded-md border border-line bg-white p-5 mb-6 grid sm:grid-cols-6 gap-3 items-end" data-testid="drum-add-form">
                <div className="sm:col-span-2">
                    <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-dim block mb-1">Product SKU</label>
                    <select data-testid="drum-sku" required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className={cellCls + " !py-2 appearance-none cursor-pointer"}>
                        <option value="">Select…</option>
                        {products.map((p) => <option key={p.sku} value={p.sku}>{p.sku} — {p.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-dim block mb-1">Brand</label>
                    <input data-testid="drum-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="KEI…" className={cellCls} />
                </div>
                <div>
                    <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-dim block mb-1">Length (m)</label>
                    <input data-testid="drum-length" required type="number" min="1" step="any" value={form.original_length} onChange={(e) => setForm({ ...form, original_length: e.target.value })} placeholder="500" className={cellCls} />
                </div>
                <div>
                    <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-dim block mb-1">Status</label>
                    <select data-testid="drum-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={cellCls + " !py-2 appearance-none cursor-pointer"}>
                        <option value="sealed">Sealed</option>
                        <option value="open">Open</option>
                    </select>
                </div>
                <button data-testid="drum-add-btn" disabled={adding} className="rounded-full bg-ink text-white font-mono uppercase tracking-[0.15em] text-[10px] py-2.5 hover:bg-copper transition-colors disabled:opacity-50">
                    {adding ? "Adding…" : "Add Drum"}
                </button>
            </form>

            {drums.length === 0 ? (
                <div className="border border-line bg-white rounded-md p-12 text-center" data-testid="drums-empty">
                    <p className="font-display font-semibold text-2xl text-ink mb-2">No drums registered yet</p>
                    <p className="text-mist text-sm">Add drums above — the cutting planner uses this register for availability.</p>
                </div>
            ) : (
                <div className="border border-line bg-white rounded-md overflow-x-auto" data-testid="drums-table">
                    <table className="w-full min-w-[760px] text-sm">
                        <thead>
                            <tr className="border-b border-line bg-paper2">
                                {["Drum", "SKU", "Brand", "Original (m)", "Balance (m)", "Reserved (m)", "Status", "Last Verified / By", ""].map((h) => (
                                    <th key={h} className="text-left font-mono text-[9px] uppercase tracking-[0.15em] text-dim px-3 py-3">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {drums.map((d) => (
                                <tr key={d.drum_id} className="border-b border-line last:border-b-0" data-testid={`drum-row-${d.drum_id}`}>
                                    <td className="px-3 py-2.5 font-mono text-xs text-ink font-semibold">{d.drum_id}</td>
                                    <td className="px-3 py-2.5 font-mono text-xs text-mist">{d.sku}</td>
                                    <td className="px-3 py-2.5 text-xs text-mist">{d.brand || "—"}</td>
                                    <td className="px-3 py-2.5 font-mono text-xs">{d.original_length}</td>
                                    <td className="px-3 py-2.5 w-28">
                                        <input data-testid={`drum-balance-${d.drum_id}`} type="number" min="0" step="any" defaultValue={d.balance} key={d.drum_id + d.balance}
                                            onBlur={(e) => {
                                                const v = parseFloat(e.target.value);
                                                if (isNaN(v) || v === d.balance) return;
                                                const reason = window.prompt(`Reason for changing ${d.drum_id} balance to ${v}m (goes to audit log):`, "physical stock check");
                                                if (reason === null) { e.target.value = d.balance; return; }
                                                onUpdate(d.drum_id, { balance: v, reason });
                                            }}
                                            className={cellCls + " !w-24"} />
                                    </td>
                                    <td className="px-3 py-2.5 font-mono text-xs">{d.reserved || 0}</td>
                                    <td className="px-3 py-2.5">
                                        <select data-testid={`drum-status-${d.drum_id}`} value={d.status} onChange={(e) => onUpdate(d.drum_id, { status: e.target.value, reason: "status change" })}
                                            className={cellCls + " !w-24 !py-1.5 appearance-none cursor-pointer"}>
                                            {["sealed", "open", "empty"].map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2.5 font-mono text-[10px] text-dim" data-testid={`drum-verified-${d.drum_id}`}>
                                        {(d.verified_at || d.updated_at || "").slice(0, 10) || "—"}{d.updated_by ? ` · ${d.updated_by}` : ""}
                                    </td>
                                    <td className="px-3 py-2.5 text-center">
                                        <button data-testid={`drum-delete-${d.drum_id}`} onClick={() => onDelete(d.drum_id)} className="text-dim hover:text-red-600" aria-label="Delete drum">
                                            <Trash2 size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function EnquiryCard({ e, onQuote, onDelete, onStage, onNote, onFollowUp }) {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState("");
    const [fuDate, setFuDate] = useState(e.follow_up_at || "");
    const [fuNote, setFuNote] = useState(e.follow_up_note || "");
    const stage = e.stage || "new";
    const due = e.follow_up_at && !["won", "lost"].includes(stage) && e.follow_up_at <= new Date().toISOString().slice(0, 10);

    return (
        <div className="p-5 md:p-6" data-testid={`admin-enquiry-row-${e.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-copper bg-copper-light border border-copper/30 px-2 py-1 rounded-full">{e.interest}</span>
                    <select
                        data-testid={`enquiry-stage-${e.id}`}
                        value={stage}
                        onChange={(ev) => onStage(e.id, ev.target.value)}
                        className={`font-mono text-[10px] uppercase tracking-[0.12em] border px-2 py-1 rounded-full cursor-pointer focus:outline-none appearance-none ${STAGE_STYLES[stage]}`}
                    >
                        {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                    </select>
                    {due && (
                        <span className="font-mono text-[9px] uppercase tracking-[0.12em] bg-red-50 border border-red-300 text-red-700 px-2 py-1 rounded-full" data-testid={`enquiry-due-${e.id}`}>
                            Follow-up due
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-4">
                    <button
                        data-testid={`admin-quote-btn-${e.id}`}
                        onClick={() => onQuote(e)}
                        className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink border border-line rounded-full px-3 py-1.5 hover:border-copper hover:text-copper transition-colors"
                    >
                        <FileText size={11} /> Quote
                    </button>
                    <button
                        data-testid={`admin-expand-btn-${e.id}`}
                        onClick={() => setOpen(!open)}
                        className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-mist hover:text-copper transition-colors"
                    >
                        {open ? "Less" : `CRM${(e.notes?.length || e.follow_up_at) ? ` (${(e.notes?.length || 0)})` : ""}`}
                    </button>
                    <button data-testid={`admin-delete-btn-${e.id}`} onClick={() => onDelete(e.id)} aria-label="Delete enquiry" className="text-dim hover:text-red-600 transition-colors">
                        <Trash2 size={14} />
                    </button>
                    <span className="font-mono text-[10px] tracking-[0.15em] text-dim uppercase">{fmt(e.created_at)}</span>
                </div>
            </div>
            <p className="font-display font-semibold text-xl text-ink leading-snug">
                {e.name}
                {e.company && (
                    <span className="flex items-center gap-2 text-mist text-sm font-body font-normal mt-1">
                        <Building2 size={12} className="text-copper" /> {e.company}
                    </span>
                )}
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 font-mono text-xs text-mist">
                {e.size && <span className="text-copper tracking-[0.1em]">SIZE: {e.size}</span>}
                <span className="flex items-center gap-2"><Phone size={12} className="text-copper" /> {e.phone}</span>
                {e.email && <span className="flex items-center gap-2"><Mail size={12} className="text-copper" /> {e.email}</span>}
            </div>
            {e.message && (
                <p className="mt-3 text-sm text-mist leading-relaxed border-l-2 border-copper/40 pl-4 whitespace-pre-line">{e.message}</p>
            )}

            {open && (
                <div className="mt-5 rounded-md border border-line bg-paper2 p-4 md:p-5 space-y-5" data-testid={`enquiry-crm-${e.id}`}>
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">Notes</p>
                        {(e.notes || []).length === 0 ? (
                            <p className="text-dim text-xs mb-2">No notes yet.</p>
                        ) : (
                            <div className="space-y-2 mb-3">
                                {e.notes.map((n, i) => (
                                    <p key={i} className="text-xs text-mist bg-white border border-line rounded-md px-3 py-2" data-testid={`enquiry-note-${e.id}-${i}`}>
                                        {n.text} <span className="text-dim font-mono text-[9px] ml-2">{fmt(n.at)}</span>
                                    </p>
                                ))}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <input
                                data-testid={`enquiry-note-input-${e.id}`}
                                value={note}
                                onChange={(ev) => setNote(ev.target.value)}
                                placeholder="Add a note (call outcome, promise, context)…"
                                className={cellCls + " !py-2"}
                                onKeyDown={(ev) => { if (ev.key === "Enter" && note.trim()) { onNote(e.id, note); setNote(""); } }}
                            />
                            <button
                                data-testid={`enquiry-note-add-${e.id}`}
                                onClick={() => { if (note.trim()) { onNote(e.id, note); setNote(""); } }}
                                className="rounded-full bg-ink text-white font-mono text-[10px] uppercase tracking-[0.15em] px-4 hover:bg-copper transition-colors"
                            >
                                Add
                            </button>
                        </div>
                    </div>
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">Follow-up reminder</p>
                        <div className="flex flex-wrap gap-2">
                            <input data-testid={`enquiry-fu-date-${e.id}`} type="date" value={fuDate} onChange={(ev) => setFuDate(ev.target.value)} className={cellCls + " !w-40 !py-2"} />
                            <input data-testid={`enquiry-fu-note-${e.id}`} value={fuNote} onChange={(ev) => setFuNote(ev.target.value)} placeholder="Reminder note (optional)" className={cellCls + " flex-1 !py-2 min-w-40"} />
                            <button
                                data-testid={`enquiry-fu-save-${e.id}`}
                                onClick={() => onFollowUp(e.id, fuDate || null, fuNote)}
                                className="rounded-full border border-line font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2 text-mist hover:border-copper hover:text-copper transition-colors"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                    {(e.history || []).length > 0 && (
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">History</p>
                            <div className="space-y-1">
                                {e.history.map((h, i) => (
                                    <p key={i} className="font-mono text-[10px] text-dim" data-testid={`enquiry-history-${e.id}-${i}`}>
                                        {fmt(h.at)} — {h.text}
                                    </p>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function CuttingTab({ jobs, onApprove, approving }) {
    if (jobs.length === 0) {
        return (
            <div className="border border-line bg-white rounded-md p-12 text-center" data-testid="cutting-jobs-empty">
                <p className="font-display font-semibold text-2xl text-ink mb-2">No cutting enquiries yet</p>
                <p className="text-mist text-sm">Customer cutting plans from the /cutting page land here for approval.</p>
            </div>
        );
    }
    return (
        <div className="border border-line bg-white rounded-md divide-y divide-line overflow-hidden" data-testid="cutting-jobs-list">
            {jobs.map((j) => (
                <div key={j.job_id} className="p-5 md:p-6" data-testid={`cutting-job-${j.job_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-3">
                            <span className="font-mono text-xs text-ink font-semibold">{j.job_id}</span>
                            <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2 py-1 rounded-full ${j.status === "approved" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-copper/40 bg-copper-light text-copper"}`} data-testid={`cutting-job-status-${j.job_id}`}>
                                {j.status}
                            </span>
                        </div>
                        <span className="font-mono text-[10px] text-dim uppercase">{fmt(j.created_at)}</span>
                    </div>
                    <p className="font-display font-semibold text-lg text-ink">
                        {j.plan?.product || j.sku}
                        <span className="text-mist text-sm font-body font-normal ml-3">
                            {j.customer?.name}{j.customer?.phone ? ` · ${j.customer.phone}` : ""}
                        </span>
                    </p>
                    <p className="font-mono text-xs text-mist mt-2">
                        Cuts: {(j.cuts || []).map((c) => `${c.length} m × ${c.qty}`).join(", ")} · Total {j.plan?.total_length} m
                    </p>
                    {j.estimate && (
                        <p className="font-mono text-xs text-copper mt-1" data-testid={`cutting-job-est-${j.job_id}`}>
                            Illustrative: ₹{j.estimate.cable_value?.toLocaleString("en-IN")} + GST ₹{j.estimate.gst_18?.toLocaleString("en-IN")} = ₹{j.estimate.total?.toLocaleString("en-IN")}
                        </p>
                    )}
                    {(j.plan?.drums_used || []).length > 0 && (
                        <div className="mt-3 space-y-1.5">
                            {j.plan.drums_used.map((d) => (
                                <p key={d.drum_id} className="font-mono text-[11px] text-mist" data-testid={`cutting-job-drum-${j.job_id}-${d.drum_id}`}>
                                    {d.drum_id}: cuts {d.cuts.map((c) => `${c} m`).join(" + ")} → leftover {d.leftover} m ({d.leftover_class === "reusable_balance" ? "reusable" : "scrap"})
                                </p>
                            ))}
                        </div>
                    )}
                    {j.status === "submitted" && (
                        <div className="mt-4">
                            {j.plan?.fully_covered ? (
                                <button
                                    data-testid={`cutting-approve-${j.job_id}`}
                                    onClick={() => onApprove(j.job_id)}
                                    disabled={approving === j.job_id}
                                    className="rounded-full bg-ink text-white font-mono uppercase tracking-[0.15em] text-[10px] px-5 py-2.5 hover:bg-copper transition-colors disabled:opacity-50"
                                >
                                    {approving === j.job_id ? "Approving…" : "Approve & Deduct Stock"}
                                </button>
                            ) : (
                                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-amber-700" data-testid={`cutting-not-covered-${j.job_id}`}>
                                    Not fully covered by register — add/adjust drums before approval
                                </p>
                            )}
                        </div>
                    )}
                    {j.status === "approved" && (j.deductions || []).length > 0 && (
                        <p className="mt-3 font-mono text-[10px] text-emerald-700" data-testid={`cutting-deducted-${j.job_id}`}>
                            Deducted: {j.deductions.map((d) => `${d.meters} m from ${d.drum_id}`).join(" · ")}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}

const IMAGE_SLOTS = [
    ...FAMILIES.map((f) => ({ slot: f.slug, label: f.name, kind: "Family" })),
    { slot: "founder", label: "Founder — Mr. Uttam Chand", kind: "About" },
    { slot: "about-shop", label: "Shop photo (About section)", kind: "About" },
    { slot: "about-warehouse", label: "Warehouse photo (About section)", kind: "About" },
];

function SiteContentTab() {
    const [assets, setAssets] = useState({ images: {}, profile: { hours: "", gstin: "", reviews_url: "", clients: [] } });
    const [profile, setProfile] = useState({ hours: "", gstin: "", reviews_url: "", clients: "" });
    const [uploading, setUploading] = useState(null);
    const [saving, setSaving] = useState(false);

    const loadAssets = () =>
        api.get("/site-assets")
            .then((res) => {
                setAssets(res.data);
                const p = res.data.profile || {};
                setProfile({
                    hours: p.hours || "",
                    gstin: p.gstin || "",
                    reviews_url: p.reviews_url || "",
                    clients: (p.clients || []).join("\n"),
                });
            })
            .catch(() => {});

    useEffect(() => {
        loadAssets();
    }, []);

    const upload = async (slot, file) => {
        if (!file) return;
        setUploading(slot);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/admin/site-assets/image/${slot}`, fd, {
                headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
            });
            toast.success("Photo uploaded", { description: IMAGE_SLOTS.find((s) => s.slot === slot)?.label });
            refreshSiteAssets();
            loadAssets();
        } catch (err) {
            toast.error(errText(err));
        } finally {
            setUploading(null);
        }
    };

    const saveProfile = async () => {
        setSaving(true);
        try {
            await api.put(
                "/admin/site-profile",
                {
                    hours: profile.hours,
                    gstin: profile.gstin,
                    reviews_url: profile.reviews_url,
                    clients: profile.clients.split("\n"),
                },
                { headers: authHeader() }
            );
            toast.success("Site details saved");
            refreshSiteAssets();
        } catch (err) {
            toast.error(errText(err));
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "w-full bg-white border border-line rounded-md px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-copper transition-colors duration-300";
    const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-dim block mb-1.5";

    return (
        <div data-testid="site-content-tab" className="space-y-10">
            <div>
                <p className="font-display font-semibold text-xl text-ink mb-1">Photos</p>
                <p className="text-mist text-sm mb-5">Upload your own photos — they appear on the site immediately. JPG, PNG or WebP, up to 8 MB.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {IMAGE_SLOTS.map((s) => {
                        const has = !!assets.images?.[s.slot];
                        return (
                            <div key={s.slot} className="rounded-md border border-line bg-white p-4" data-testid={`slot-card-${s.slot}`}>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-14 h-14 rounded-md border border-line overflow-hidden bg-paper2 shrink-0">
                                        {has && <img src={siteImageUrl(s.slot) + `?v=${Date.now()}`} alt={s.label} className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm text-ink font-medium leading-snug">{s.label}</p>
                                        <p className={`font-mono text-[9px] uppercase tracking-[0.15em] ${has ? "text-emerald-600" : "text-dim"}`}>
                                            {has ? "Photo set" : "Placeholder showing"}
                                        </p>
                                    </div>
                                </div>
                                <label
                                    className={`block text-center rounded-full border border-line py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] cursor-pointer hover:border-copper hover:text-copper transition-colors duration-300 ${uploading === s.slot ? "opacity-50 pointer-events-none" : ""}`}
                                    data-testid={`slot-upload-${s.slot}`}
                                >
                                    {uploading === s.slot ? "Uploading…" : has ? "Replace photo" : "Upload photo"}
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        className="hidden"
                                        onChange={(e) => {
                                            upload(s.slot, e.target.files?.[0]);
                                            e.target.value = "";
                                        }}
                                    />
                                </label>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div>
                <p className="font-display font-semibold text-xl text-ink mb-1">Business details</p>
                <p className="text-mist text-sm mb-5">Shown in the Visit Us section above the footer. Leave blank to hide.</p>
                <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
                    <div>
                        <label className={labelCls}>Opening hours</label>
                        <input data-testid="profile-hours" value={profile.hours} onChange={(e) => setProfile({ ...profile, hours: e.target.value })} placeholder="e.g. Mon–Sat · 10 AM – 7:30 PM" className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>GST number</label>
                        <input data-testid="profile-gstin" value={profile.gstin} onChange={(e) => setProfile({ ...profile, gstin: e.target.value })} placeholder="e.g. 29XXXXX0000X1Z5" className={inputCls} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={labelCls}>Google reviews link</label>
                        <input data-testid="profile-reviews" value={profile.reviews_url} onChange={(e) => setProfile({ ...profile, reviews_url: e.target.value })} placeholder="https://g.page/r/… (from your Google Business Profile)" className={inputCls} />
                    </div>
                    <div className="sm:col-span-2">
                        <label className={labelCls}>Clients & projects — one per line</label>
                        <textarea data-testid="profile-clients" value={profile.clients} onChange={(e) => setProfile({ ...profile, clients: e.target.value })} rows={4} placeholder={"Example Builder Pvt Ltd\nMetro Rail Project\nABC Industries"} className={inputCls} />
                    </div>
                </div>
                <button
                    data-testid="profile-save"
                    onClick={saveProfile}
                    disabled={saving}
                    className="mt-4 rounded-full bg-ink text-white font-mono uppercase tracking-[0.15em] text-[11px] px-8 py-3 hover:bg-copper transition-colors duration-300 disabled:opacity-50"
                >
                    {saving ? "Saving…" : "Save details"}
                </button>
            </div>
        </div>
    );
}

const EMPTY_PRICE_FORM = {
    sku: "", category: "aluminium-armoured", brand: "", size_label: "", cores: "", size_sqmm: "",
    cable_type: "", conductor: "", armoured: "", description: "", lp: "", flat_price: "",
    unit: "metre", moq: "", stock: "", hsn: "", gst_percent: 18, status: "active",
};

function PriceManagerTab() {
    const [items, setItems] = useState([]);
    const [cats, setCats] = useState([]);
    const [form, setForm] = useState(EMPTY_PRICE_FORM);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [upload, setUpload] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [batches, setBatches] = useState([]);
    const [settings, setSettings] = useState({ show_lp: false, whatsapp: "" });
    const [filter, setFilter] = useState("");

    const inputCls = "w-full bg-white border border-line rounded-md px-3.5 py-2.5 text-sm text-ink focus:outline-none focus:border-copper transition-colors duration-300";
    const labelCls = "font-mono text-[9px] uppercase tracking-[0.2em] text-dim block mb-1";

    const load = () => {
        api.get("/admin/prices", { headers: authHeader() })
            .then((res) => { setItems(res.data.items); setCats(res.data.categories); })
            .catch((err) => toast.error(errText(err)));
        api.get("/admin/prices/batches", { headers: authHeader() })
            .then((res) => setBatches(res.data.batches))
            .catch(() => {});
        api.get("/prices/settings").then((res) => setSettings(res.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const saveSettings = async () => {
        try {
            await api.put("/admin/pricing-settings", { show_lp: settings.show_lp, whatsapp: settings.whatsapp }, { headers: authHeader() });
            toast.success("Price display settings saved");
        } catch (err) { toast.error(errText(err)); }
    };

    const saveItem = async () => {
        if (!form.sku.trim() || form.flat_price === "") {
            toast.error("SKU and flat price are required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                lp: form.lp === "" ? null : parseFloat(form.lp),
                flat_price: parseFloat(form.flat_price),
                moq: form.moq === "" ? 0 : parseFloat(form.moq),
                stock: form.stock === "" ? null : parseFloat(form.stock),
                gst_percent: parseFloat(form.gst_percent) || 18,
            };
            if (editing) {
                const res = await api.put(`/admin/prices/${editing}`, payload, { headers: authHeader() });
                toast.success(`Updated ${editing}`);
                if (res.data.warning) toast.warning(res.data.warning, { duration: 8000 });
            } else {
                await api.post("/admin/prices", payload, { headers: authHeader() });
                toast.success(`Added ${payload.sku}`);
            }
            setForm(EMPTY_PRICE_FORM);
            setEditing(null);
            load();
        } catch (err) { toast.error(errText(err)); } finally { setSaving(false); }
    };

    const toggleStatus = async (it) => {
        try {
            await api.put(`/admin/prices/${it.sku}`, { ...it, lp: it.lp, stock: it.stock, status: it.status === "active" ? "inactive" : "active" }, { headers: authHeader() });
            toast.success(`${it.sku} → ${it.status === "active" ? "inactive" : "active"}`);
            load();
        } catch (err) { toast.error(errText(err)); }
    };

    const doUpload = async (file) => {
        if (!file) return;
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post("/admin/prices/upload", fd, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } });
            setUpload(res.data);
            toast.success(`Parsed ${res.data.total} rows — review below, nothing saved yet`);
        } catch (err) { toast.error(errText(err)); }
    };

    const confirmUpload = async () => {
        setConfirming(true);
        try {
            const res = await api.post(`/admin/prices/confirm/${upload.upload_id}`, {}, { headers: authHeader() });
            toast.success(`Applied — ${res.data.changed} products updated/added`);
            setUpload(null);
            load();
        } catch (err) { toast.error(errText(err)); } finally { setConfirming(false); }
    };

    const rollback = async (batchId) => {
        try {
            const res = await api.post(`/admin/prices/rollback/${batchId}`, {}, { headers: authHeader() });
            toast.success(`Rolled back — ${res.data.restored} products restored`);
            load();
        } catch (err) { toast.error(errText(err)); }
    };

    const downloadFile = async (path, filename) => {
        try {
            const res = await api.get(path, { headers: authHeader(), responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error(errText(err)); }
    };

    const filtered = items.filter((i) => !filter || (i.sku + i.description + i.brand).toLowerCase().includes(filter.toLowerCase()));

    return (
        <div data-testid="prices-tab" className="space-y-10">
            {/* settings */}
            <div className="rounded-md border border-line bg-white p-5 flex flex-wrap items-end gap-4">
                <div>
                    <label className={labelCls}>Show LP (list price) to customers</label>
                    <button
                        data-testid="pricing-show-lp-toggle"
                        onClick={() => setSettings({ ...settings, show_lp: !settings.show_lp })}
                        className={`rounded-full px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] border transition-colors duration-300 ${settings.show_lp ? "bg-copper border-copper text-white" : "border-line text-mist"}`}
                    >
                        {settings.show_lp ? "LP visible" : "LP hidden"}
                    </button>
                </div>
                <div className="w-56">
                    <label className={labelCls}>WhatsApp business number</label>
                    <input data-testid="pricing-whatsapp" value={settings.whatsapp} onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })} className={inputCls} placeholder="919008177176" />
                </div>
                <button data-testid="pricing-settings-save" onClick={saveSettings} className="rounded-full bg-ink text-white font-mono uppercase tracking-[0.15em] text-[10px] px-6 py-2.5 hover:bg-copper transition-colors duration-300">
                    Save settings
                </button>
            </div>

            {/* bulk tools */}
            <div className="rounded-md border border-line bg-white p-5">
                <p className="font-display font-semibold text-lg text-ink mb-1">Bulk Excel upload</p>
                <p className="text-mist text-sm mb-4">Download the template, fill in LP and flat prices, upload, review the preview, then confirm. Nothing is saved until you confirm.</p>
                <div className="flex flex-wrap gap-3">
                    <button data-testid="pricing-template-btn" onClick={() => downloadFile("/admin/prices/template", "roxy-price-list-template.xlsx")} className="rounded-full border border-line px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink hover:border-copper hover:text-copper transition-colors duration-300 flex items-center gap-2">
                        <Download size={13} /> Template
                    </button>
                    <label data-testid="pricing-upload-btn" className="rounded-full bg-copper text-white px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] cursor-pointer hover:bg-copper-bright transition-colors duration-300 flex items-center gap-2">
                        <Plus size={13} /> Upload price list
                        <input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                    <button data-testid="pricing-export-btn" onClick={() => downloadFile("/admin/prices/export", "roxy-price-list-export.xlsx")} className="rounded-full border border-line px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink hover:border-copper hover:text-copper transition-colors duration-300 flex items-center gap-2">
                        <FileSpreadsheet size={13} /> Export current list
                    </button>
                    <button data-testid="pricing-dealer-pdf-btn" onClick={() => downloadFile("/admin/prices/dealer-list.pdf", "roxy-cables-dealer-price-list-INTERNAL.pdf")} className="rounded-full border border-amber-500/50 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-600 hover:bg-amber-500/10 transition-colors duration-300 flex items-center gap-2">
                        <Download size={13} /> Dealer copy (LP)
                    </button>
                </div>

                {upload && (
                    <div className="mt-5 border-t border-line pt-5" data-testid="pricing-upload-preview">
                        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink mb-3">
                            Preview: {upload.valid} valid ({upload.new} new, {upload.updates} updates) · {upload.errors.length} with errors
                        </p>
                        {upload.errors.length > 0 && (
                            <div className="rounded-md border border-red-200 bg-red-50 p-4 mb-4 max-h-40 overflow-y-auto" data-testid="pricing-upload-errors">
                                {upload.errors.map((e) => (
                                    <p key={e.row} className="text-xs text-red-700">Row {e.row} ({e.sku || "no SKU"}): {e.errors.join(", ")}</p>
                                ))}
                            </div>
                        )}
                        <div className="max-h-64 overflow-y-auto rounded-md border border-line mb-4">
                            <table className="w-full text-xs">
                                <thead><tr className="bg-paper2 border-b border-line">
                                    {["Row", "SKU", "Size", "LP", "Flat", "Unit", "Action", "Current"].map((h) => (
                                        <th key={h} className="px-3 py-2 text-left font-mono text-[9px] uppercase tracking-[0.15em] text-dim">{h}</th>
                                    ))}
                                </tr></thead>
                                <tbody>
                                    {upload.rows.map((r) => (
                                        <tr key={r.row} className="border-b border-line last:border-b-0">
                                            <td className="px-3 py-2 font-mono">{r.row}</td>
                                            <td className="px-3 py-2 font-mono">{r.data.sku}</td>
                                            <td className="px-3 py-2">{r.data.size_label}</td>
                                            <td className="px-3 py-2 font-mono">{r.data.lp ?? "—"}</td>
                                            <td className="px-3 py-2 font-mono font-medium">{r.data.flat_price}</td>
                                            <td className="px-3 py-2">{r.data.unit}</td>
                                            <td className="px-3 py-2">
                                                <span className={`font-mono text-[9px] uppercase px-2 py-0.5 rounded-full ${r.action === "new" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.action}</span>
                                            </td>
                                            <td className="px-3 py-2 font-mono text-dim">
                                                {r.current ? `₹${r.current.flat_price}` : "—"}
                                                {r.warning && <span className="block mt-1 font-mono text-[9px] normal-case tracking-normal text-amber-600" data-testid={`upload-warning-row-${r.row}`}>{r.warning}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex gap-3">
                            <button data-testid="pricing-confirm-upload" onClick={confirmUpload} disabled={confirming || upload.valid === 0} className="rounded-full bg-emerald-600 text-white px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-emerald-700 transition-colors duration-300 disabled:opacity-50">
                                {confirming ? "Saving…" : `Confirm & save ${upload.valid} rows`}
                            </button>
                            <button data-testid="pricing-discard-upload" onClick={() => setUpload(null)} className="rounded-full border border-line px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-mist hover:border-red-300 hover:text-red-500 transition-colors duration-300">
                                Discard
                            </button>
                        </div>
                    </div>
                )}

                {batches.length > 0 && (
                    <div className="mt-5 border-t border-line pt-4">
                        <p className={labelCls}>Recent bulk batches — roll back if a mistake slipped in</p>
                        <div className="space-y-2">
                            {batches.slice(0, 5).map((b) => (
                                <div key={b.id} className="flex items-center justify-between gap-3 text-xs font-mono text-mist">
                                    <span>{fmt(b.created_at)} · {b.filename}</span>
                                    {b.rolled_back ? (
                                        <span className="text-dim uppercase tracking-[0.15em] text-[9px]">Rolled back</span>
                                    ) : (
                                        <button data-testid={`pricing-rollback-${b.id}`} onClick={() => rollback(b.id)} className="text-red-500 hover:text-red-700 uppercase tracking-[0.15em] text-[9px]">
                                            Roll back
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* add / edit form */}
            <div className="rounded-md border border-line bg-white p-5">
                <p className="font-display font-semibold text-lg text-ink mb-4">{editing ? `Edit ${editing}` : "Add a product"}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div><label className={labelCls}>SKU / Product code *</label><input data-testid="pf-sku" value={form.sku} disabled={!!editing} onChange={(e) => setForm({ ...form, sku: e.target.value })} className={`${inputCls} disabled:opacity-50`} placeholder="CU-AR-16X4C" /></div>
                    <div>
                        <label className={labelCls}>Category *</label>
                        <select data-testid="pf-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${inputCls} appearance-none`}>
                            {cats.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                        </select>
                    </div>
                    <div><label className={labelCls}>Brand</label><input data-testid="pf-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} placeholder="KEI" /></div>
                    <div><label className={labelCls}>Size label</label><input data-testid="pf-size-label" value={form.size_label} onChange={(e) => setForm({ ...form, size_label: e.target.value })} className={inputCls} placeholder="16 x 4 Core" /></div>
                    <div><label className={labelCls}>Cores</label><input value={form.cores} onChange={(e) => setForm({ ...form, cores: e.target.value })} className={inputCls} placeholder="4C" /></div>
                    <div><label className={labelCls}>Size (sqmm)</label><input value={form.size_sqmm} onChange={(e) => setForm({ ...form, size_sqmm: e.target.value })} className={inputCls} placeholder="16" /></div>
                    <div><label className={labelCls}>Cable type</label><input value={form.cable_type} onChange={(e) => setForm({ ...form, cable_type: e.target.value })} className={inputCls} placeholder="XLPE Armoured" /></div>
                    <div>
                        <label className={labelCls}>Conductor</label>
                        <select value={form.conductor} onChange={(e) => setForm({ ...form, conductor: e.target.value })} className={`${inputCls} appearance-none`}>
                            <option value="">—</option><option>Copper</option><option>Aluminium</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Armoured</label>
                        <select value={form.armoured} onChange={(e) => setForm({ ...form, armoured: e.target.value })} className={`${inputCls} appearance-none`}>
                            <option value="">—</option><option>Armoured</option><option>Unarmoured</option>
                        </select>
                    </div>
                    <div className="sm:col-span-2"><label className={labelCls}>Description</label><input data-testid="pf-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} placeholder="16 x 4 Core Copper Armoured Cable" /></div>
                    <div><label className={labelCls}>LP (list price)</label><input data-testid="pf-lp" type="number" min="0" step="any" value={form.lp} onChange={(e) => setForm({ ...form, lp: e.target.value })} className={inputCls} /></div>
                    <div><label className={labelCls}>Flat selling price *</label><input data-testid="pf-flat" type="number" min="0" step="any" value={form.flat_price} onChange={(e) => setForm({ ...form, flat_price: e.target.value })} className={inputCls} /></div>
                    <div>
                        <label className={labelCls}>Unit</label>
                        <select data-testid="pf-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={`${inputCls} appearance-none`}>
                            {["metre", "coil", "roll", "piece"].map((u) => <option key={u}>{u}</option>)}
                        </select>
                    </div>
                    <div><label className={labelCls}>MOQ</label><input type="number" min="0" step="any" value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} className={inputCls} /></div>
                    <div><label className={labelCls}>Stock (optional)</label><input type="number" min="0" step="any" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputCls} /></div>
                    <div><label className={labelCls}>HSN code</label><input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} className={inputCls} placeholder="8544" /></div>
                    <div><label className={labelCls}>GST %</label><input type="number" min="0" max="28" step="any" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className={inputCls} /></div>
                    <div>
                        <label className={labelCls}>Status</label>
                        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={`${inputCls} appearance-none`}>
                            <option value="active">Active</option><option value="inactive">Inactive</option>
                        </select>
                    </div>
                </div>
                <div className="flex gap-3 mt-4">
                    <button data-testid="pf-save" onClick={saveItem} disabled={saving} className="rounded-full bg-ink text-white font-mono uppercase tracking-[0.15em] text-[10px] px-7 py-3 hover:bg-copper transition-colors duration-300 disabled:opacity-50">
                        {saving ? "Saving…" : editing ? "Save changes" : "Add product"}
                    </button>
                    {editing && (
                        <button data-testid="pf-cancel" onClick={() => { setForm(EMPTY_PRICE_FORM); setEditing(null); }} className="rounded-full border border-line font-mono uppercase tracking-[0.15em] text-[10px] px-7 py-3 text-mist hover:border-copper hover:text-copper transition-colors duration-300">
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            {/* items table */}
            <div className="rounded-md border border-line bg-white p-5">
                <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                    <p className="font-display font-semibold text-lg text-ink">Price list <span className="text-dim font-mono text-xs">({filtered.length})</span></p>
                    <input data-testid="pricing-search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search SKU / description…" className={`${inputCls} max-w-xs`} />
                </div>
                <div className="overflow-x-auto max-h-[480px] overflow-y-auto rounded-md border border-line">
                    <table className="w-full min-w-[860px] text-xs" data-testid="pricing-table">
                        <thead className="sticky top-0 bg-paper2">
                            <tr className="border-b border-line">
                                {["SKU", "Category", "Size", "Brand", "LP", "Flat", "Unit", "GST", "Status", "Updated", ""].map((h) => (
                                    <th key={h} className="px-3 py-2.5 text-left font-mono text-[9px] uppercase tracking-[0.15em] text-dim">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((it) => (
                                <tr key={it.sku} className="border-b border-line last:border-b-0" data-testid={`pricing-row-${it.sku.toLowerCase()}`}>
                                    <td className="px-3 py-2.5 font-mono">{it.sku}</td>
                                    <td className="px-3 py-2.5">{it.category_label}</td>
                                    <td className="px-3 py-2.5">{it.size_label}</td>
                                    <td className="px-3 py-2.5">{it.brand}</td>
                                    <td className="px-3 py-2.5 font-mono">{it.lp != null ? `₹${it.lp}` : "—"}</td>
                                    <td className="px-3 py-2.5 font-mono font-semibold text-ink">₹{it.flat_price}</td>
                                    <td className="px-3 py-2.5">{it.unit}</td>
                                    <td className="px-3 py-2.5">{it.gst_percent}%</td>
                                    <td className="px-3 py-2.5">
                                        <span className={`font-mono text-[9px] uppercase px-2 py-0.5 rounded-full ${it.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>{it.status}</span>
                                    </td>
                                    <td className="px-3 py-2.5 font-mono text-dim" title={`${(it.history || []).length} price changes`}>{fmt(it.updated_at)}</td>
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <button data-testid={`pricing-edit-${it.sku.toLowerCase()}`} onClick={() => { setEditing(it.sku); setForm({ ...EMPTY_PRICE_FORM, ...it, lp: it.lp ?? "", stock: it.stock ?? "", moq: it.moq || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="font-mono text-[9px] uppercase tracking-[0.15em] text-copper hover:text-copper-bright mr-3">Edit</button>
                                        <button data-testid={`pricing-toggle-${it.sku.toLowerCase()}`} onClick={() => toggleStatus(it)} className="font-mono text-[9px] uppercase tracking-[0.15em] text-dim hover:text-red-500">{it.status === "active" ? "Deactivate" : "Activate"}</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function DashboardTab() {
    const [d, setD] = useState(null);

    const load = () => {
        api.get("/admin/dashboard", { headers: authHeader() })
            .then((res) => setD(res.data))
            .catch((err) => toast.error(errText(err)));
    };
    useEffect(() => { load(); }, []);

    if (!d) {
        return <div className="rounded-md border border-line bg-white p-12 text-center text-mist font-mono text-[11px] uppercase tracking-[0.2em] animate-pulse" data-testid="dashboard-loading">Loading command deck…</div>;
    }

    const stat = (label, value, sub, testid, accent = "text-ink") => (
        <div className="border border-line bg-white rounded-md p-5 shadow-sm" data-testid={testid}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">{label}</p>
            <p className={`font-display font-bold text-3xl ${accent}`}>{value}</p>
            {sub && <p className="font-mono text-[10px] text-dim mt-1.5 uppercase tracking-[0.12em]">{sub}</p>}
        </div>
    );

    const maxTrend = Math.max(1, ...d.trend.map((t) => t.count));

    return (
        <div className="space-y-6" data-testid="dashboard-tab">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {stat("Quotation requests", d.quotes.total, `${d.quotes.today} today · ${d.quotes.week} this week`, "dash-quotes", "text-copper")}
                {stat("Quote conversion", d.quotes.conversion_rate === null ? "—" : d.quotes.conversion_rate + "%", d.quotes.conversion_rate === null ? "no converted/lost yet" : `${d.quotes.by_status.converted} converted · ${d.quotes.by_status.lost} lost`, "dash-conversion")}
                {stat("Pending enquiries", d.enquiries.pending, d.enquiries.follow_ups_due ? `${d.enquiries.follow_ups_due} follow-ups due` : `${d.enquiries.total} total`, "dash-pending", d.enquiries.pending > 0 ? "text-copper" : "text-ink")}
                {stat("Pipeline value", inr(d.pipeline_value), `${d.opportunities.length} open estimates`, "dash-pipeline")}
                {stat("Repeat customers", d.repeat_customers.length, "2+ interactions", "dash-repeat")}
                {stat("Enquiry win rate", d.enquiries.win_rate === null ? "—" : d.enquiries.win_rate + "%", d.enquiries.won ? `${d.enquiries.won} won` : "no wins recorded yet", "dash-winrate")}
            </div>

            <div className="rounded-md border border-line bg-white p-5" data-testid="dash-trend">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-4">Quotations — last 14 days</p>
                <div className="flex items-end gap-1.5 h-24">
                    {d.trend.map((t) => (
                        <div key={t.date} className="flex-1 flex flex-col items-center gap-1 group">
                            <div
                                className={`w-full rounded-sm transition-all duration-300 ${t.count ? "bg-copper group-hover:bg-copper-bright" : "bg-line/60"}`}
                                style={{ height: `${Math.max(4, (t.count / maxTrend) * 88)}px` }}
                                title={`${t.date}: ${t.count}`}
                            />
                            <span className="font-mono text-[8px] text-dim hidden sm:block">{t.date.slice(3)}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <div className="rounded-md border border-line bg-white p-5" data-testid="dash-popular">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-4">Popular cables</p>
                    {d.popular_cables.length === 0 ? <p className="text-sm text-mist">No data yet.</p> : (
                        <div className="space-y-2.5">
                            {d.popular_cables.map((p, i) => (
                                <div key={p.description} className="flex items-center gap-3" data-testid={`dash-popular-${i}`}>
                                    <span className="font-mono text-[10px] text-copper w-6">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="flex-1 text-sm text-ink truncate">{p.description}</span>
                                    <span className="font-mono text-[10px] text-dim shrink-0">{p.estimates} quotes · {p.qty} units</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="rounded-md border border-line bg-white p-5" data-testid="dash-opportunities">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-4">Sales opportunities</p>
                    {d.opportunities.length === 0 ? <p className="text-sm text-mist">No open estimates right now.</p> : (
                        <div className="space-y-2.5">
                            {d.opportunities.map((o) => (
                                <div key={o.estimate_no} className="flex items-center gap-3" data-testid={`dash-opp-${o.estimate_no.toLowerCase()}`}>
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm text-ink truncate">{o.customer}{o.company && o.customer !== o.company ? ` · ${o.company}` : ""}</span>
                                        <span className="block font-mono text-[9px] text-dim uppercase">{o.estimate_no} · {o.status} · {o.age_days}d old</span>
                                    </span>
                                    <span className="font-mono text-xs text-copper shrink-0">{inr(o.total)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="rounded-md border border-line bg-white p-5" data-testid="dash-repeat-list">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-4">Repeat customers</p>
                    {d.repeat_customers.length === 0 ? <p className="text-sm text-mist">No repeat customers yet.</p> : (
                        <div className="space-y-2.5">
                            {d.repeat_customers.map((c, i) => (
                                <div key={i} className="flex items-center gap-3" data-testid={`dash-repeat-${i}`}>
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm text-ink truncate">{c.name || "—"}{c.company ? ` · ${c.company}` : ""}</span>
                                        <span className="block font-mono text-[9px] text-dim uppercase">last: {c.last || "—"}</span>
                                    </span>
                                    <span className="font-mono text-[10px] text-copper shrink-0">{c.contacts}× · {inr(c.value)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function EstimatesTab() {
    const [analytics, setAnalytics] = useState(null);
    const [estimates, setEstimates] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [q, setQ] = useState("");
    const [fStatus, setFStatus] = useState("all");
    const [fSource, setFSource] = useState("all");

    const load = () => {
        api.get("/admin/estimates/analytics", { headers: authHeader() }).then((res) => setAnalytics(res.data)).catch((err) => toast.error(errText(err)));
        api.get("/admin/estimates", { headers: authHeader(), params: { status: fStatus === "all" ? undefined : fStatus, source: fSource === "all" ? undefined : fSource, q: q || undefined } })
            .then((res) => { setEstimates(res.data.estimates); setStatuses(res.data.statuses); })
            .catch((err) => toast.error(errText(err)));
    };
    useEffect(() => { load(); }, [fStatus, fSource]);

    const setStatus = async (id, status) => {        try {
            await api.put(`/admin/estimates/${id}/status`, { status }, { headers: authHeader() });
            toast.success(`Status → ${status}`);
            load();
        } catch (err) { toast.error(errText(err)); }
    };

    const [approvingId, setApprovingId] = useState(null);
    const approve = async (id) => {
        setApprovingId(id);
        try {
            const res = await api.post(`/admin/estimates/${id}/approve`, {}, { headers: authHeader() });
            toast.success(`Approved as ${res.data.quotation_no} (v${res.data.version}) — valid ${res.data.valid_until}`, { duration: 7000 });
            load();
        } catch (err) { toast.error(errText(err)); } finally { setApprovingId(null); }
    };

    const downloadQuotation = async (quotationNo) => {
        try {
            const res = await api.get(`/admin/quotations/${quotationNo}/pdf`, { headers: authHeader(), responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${quotationNo}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error(errText(err)); }
    };

    const exportCsv = () => {
        const rows = [["Estimate", "Date", "Customer", "Phone", "Email", "Items", "Subtotal", "GST", "Total", "Source", "Status"]];
        estimates.forEach((e) => rows.push([
            e.estimate_no, (e.created_at || "").slice(0, 10), e.customer?.name || "", e.customer?.phone || "",
            e.customer?.email || "", (e.items || []).length, e.subtotal, e.gst_total, e.total, e.source, e.status,
        ]));
        const blob = new Blob([rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "roxy-estimates.csv";
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const statCards = analytics ? [
        ["Total estimates", analytics.total_estimates],
        ["Customer enquiries", analytics.enquiries],
        ["Anonymous downloads", analytics.anonymous_downloads],
        ["Estimated value", inr(analytics.total_value)],
        ["Today", analytics.today],
        ["This month", analytics.this_month],
    ] : [];

    return (
        <div data-testid="estimates-tab" className="space-y-8">
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                {statCards.map(([label, val], i) => (
                    <div key={label} className="border border-line bg-white rounded-md p-4 shadow-sm">
                        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-dim mb-1.5">{label}</p>
                        <p className="font-display font-bold text-2xl text-ink" data-testid={`est-stat-${i}`}>{val}</p>
                    </div>
                ))}
            </div>

            {analytics?.top_items?.length > 0 && (
                <div className="rounded-md border border-line bg-white p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-3">Most estimated cables</p>
                    <div className="flex flex-wrap gap-2">
                        {analytics.top_items.map((t) => (
                            <span key={t.description} className="font-mono text-[10px] border border-line rounded-full px-3 py-1.5 text-mist">
                                {t.description} · {t.qty}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="rounded-md border border-line bg-white p-5">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <input data-testid="estimates-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search estimate no / customer…" className="bg-white border border-line rounded-md px-3.5 py-2.5 text-sm text-ink focus:outline-none focus:border-copper max-w-xs w-full" />
                    <select data-testid="estimates-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="bg-white border border-line rounded-full px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.12em] text-ink appearance-none cursor-pointer">
                        <option value="all">Status: All</option>
                        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select data-testid="estimates-filter-source" value={fSource} onChange={(e) => setFSource(e.target.value)} className="bg-white border border-line rounded-full px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.12em] text-ink appearance-none cursor-pointer">
                        <option value="all">Source: All</option>
                        <option value="enquiry">Enquiries</option>
                        <option value="download">Downloads</option>
                    </select>
                    <button data-testid="estimates-refresh" onClick={load} className="rounded-full border border-line px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.12em] text-mist hover:border-copper hover:text-copper transition-colors">Apply</button>
                    <button data-testid="estimates-export" onClick={exportCsv} className="rounded-full border border-line px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.12em] text-mist hover:border-copper hover:text-copper transition-colors flex items-center gap-2">
                        <Download size={12} /> CSV
                    </button>
                </div>
                {estimates.length === 0 ? (
                    <p className="text-mist text-sm py-6 text-center" data-testid="estimates-empty">No estimates yet — they appear when customers use the price page.</p>
                ) : (
                    <div className="space-y-2">
                        {estimates.map((e) => (
                            <div key={e.id} className="border border-line rounded-md p-4 flex flex-wrap items-center gap-4" data-testid={`estimate-row-${e.estimate_no.toLowerCase()}`}>
                                <div className="min-w-[130px]">
                                    <p className="font-mono text-xs text-ink font-medium">{e.estimate_no}</p>
                                    <p className="font-mono text-[10px] text-dim">{fmt(e.created_at)}</p>
                                </div>
                                <div className="flex-1 min-w-[180px]">
                                    <p className="text-sm text-ink">{e.customer?.name || "Anonymous download"}</p>
                                    <p className="font-mono text-[10px] text-dim">{[e.customer?.phone, e.customer?.email].filter(Boolean).join(" · ") || `${(e.items || []).length} items`}</p>
                                </div>
                                <span className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${e.source === "enquiry" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                                    {e.source === "enquiry" ? "Enquiry" : "Download"}
                                </span>
                                <p className="font-mono text-sm text-ink font-semibold">{inr(e.total)}</p>
                                <select
                                    data-testid={`estimate-status-${e.estimate_no.toLowerCase()}`}
                                    value={e.status}
                                    onChange={(ev) => setStatus(e.id, ev.target.value)}
                                    className="bg-white border border-line rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-ink appearance-none cursor-pointer"
                                >
                                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                                {e.quotation_no ? (
                                    <button
                                        data-testid={`estimate-quotation-${e.estimate_no.toLowerCase()}`}
                                        onClick={() => downloadQuotation(e.quotation_no)}
                                        title="Download approved quotation PDF"
                                        className="rounded-full bg-emerald-600 text-white font-mono text-[9px] uppercase tracking-[0.12em] px-3.5 py-1.5 hover:bg-emerald-700 transition-colors duration-300 flex items-center gap-1.5"
                                    >
                                        <Download size={11} /> {e.quotation_no}
                                    </button>
                                ) : (
                                    <button
                                        data-testid={`estimate-approve-${e.estimate_no.toLowerCase()}`}
                                        onClick={() => approve(e.id)}
                                        disabled={approvingId === e.id}
                                        className="rounded-full border border-emerald-600/60 text-emerald-700 font-mono text-[9px] uppercase tracking-[0.12em] px-3.5 py-1.5 hover:bg-emerald-600 hover:text-white transition-colors duration-300 disabled:opacity-50"
                                    >
                                        {approvingId === e.id ? "Approving…" : "Approve → Quotation"}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function AdminInbox() {
    const [authed, setAuthed] = useState(!!getToken());
    const [enquiries, setEnquiries] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [tab, setTab] = useState("enquiries");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [form, setForm] = useState({ email: "", password: "" });
    const [signingIn, setSigningIn] = useState(false);
    const [quoteFor, setQuoteFor] = useState(null);
    const [emailing, setEmailing] = useState(null);
    const [drums, setDrums] = useState([]);
    const [products, setProducts] = useState([]);
    const [stageFilter, setStageFilter] = useState("all");
    const [cuttingJobs, setCuttingJobs] = useState([]);
    const [approving, setApproving] = useState(null);

    const patchEnquiry = (id, updated) => setEnquiries((p) => p.map((e) => (e.id === id ? updated : e)));

    const setStage = async (id, stage) => {
        try {
            const res = await api.put(`/enquiries/${id}/stage`, { stage }, { headers: authHeader() });
            patchEnquiry(id, res.data);
            toast.success(`Stage → ${STAGE_LABEL[stage]}`);
        } catch (err) {
            toast.error(errText(err));
        }
    };

    const addNote = async (id, text) => {
        try {
            const res = await api.post(`/enquiries/${id}/notes`, { text }, { headers: authHeader() });
            patchEnquiry(id, res.data);
            toast.success("Note added");
        } catch (err) {
            toast.error(errText(err));
        }
    };

    const saveFollowUp = async (id, date, note) => {
        try {
            const res = await api.put(`/enquiries/${id}/followup`, { follow_up_at: date, follow_up_note: note }, { headers: authHeader() });
            patchEnquiry(id, res.data);
            toast.success(date ? `Follow-up set for ${date}` : "Follow-up cleared");
        } catch (err) {
            toast.error(errText(err));
        }
    };

    const load = () => {
        setRefreshing(true);
        const hdr = { headers: authHeader() };
        Promise.all([
            api.get("/enquiries", hdr),
            api.get("/quotes", hdr),
            api.get("/drums", hdr),
            api.get("/products", { params: { limit: 50 } }),
            api.get("/cutting/jobs", hdr),
        ])
            .then(([e, q, d, p, c]) => { setEnquiries(e.data); setQuotes(q.data); setDrums(d.data); setProducts(p.data); setCuttingJobs(c.data); })
            .catch((e) => {
                if (e.response?.status === 401 || e.response?.status === 403) {
                    sessionStorage.removeItem(TOKEN_KEY);
                    setAuthed(false);
                }
            })
            .finally(() => { setLoading(false); setRefreshing(false); });
    };

    useEffect(() => {
        if (authed) load();
        else setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authed]);

    const login = async (e) => {
        e.preventDefault();
        setSigningIn(true);
        try {
            const res = await api.post("/admin/login", form);
            sessionStorage.setItem(TOKEN_KEY, res.data.token);
            setAuthed(true);
            toast.success("Signed in to the admin inbox");
        } catch (err) {
            toast.error(errText(err));
        } finally {
            setSigningIn(false);
        }
    };

    const logout = () => {
        sessionStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
        setEnquiries([]);
        setQuotes([]);
    };

    const setStatus = async (quoteId, status) => {
        try {
            await api.put(`/quotes/${quoteId}/status`, { status }, { headers: authHeader() });
            setQuotes((p) => p.map((q) => (q.quote_id === quoteId ? { ...q, status } : q)));
            toast.success(`${quoteId} marked ${status}`);
        } catch (err) {
            toast.error(errText(err));
        }
    };

    const downloadPdf = async (quoteId) => {
        try {
            const res = await api.get(`/quotes/${quoteId}/pdf`, { headers: authHeader(), responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${quoteId}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not download PDF");
        }
    };

    const emailQuote = async (quoteId) => {
        setEmailing(quoteId);
        try {
            const res = await api.post(`/quotes/${quoteId}/email`, {}, { headers: authHeader() });
            toast.success(`Quotation emailed to ${res.data.to}`, { description: `${quoteId} sent with PDF attached` });
            load();
        } catch (err) {
            toast.error(errText(err));
        } finally {
            setEmailing(null);
        }
    };

    const deleteEnquiry = async (id) => {
        if (!window.confirm("Delete this enquiry permanently?")) return;
        try {
            await api.delete(`/enquiries/${id}`, { headers: authHeader() });
            setEnquiries((p) => p.filter((e) => e.id !== id));
            toast.success("Enquiry deleted");
        } catch (err) {
            toast.error(errText(err));
        }
    };

    const deleteQuote = async (quoteId) => {
        if (!window.confirm(`Delete quotation ${quoteId} permanently?`)) return;
        try {
            await api.delete(`/quotes/${quoteId}`, { headers: authHeader() });
            setQuotes((p) => p.filter((q) => q.quote_id !== quoteId));
            toast.success(`${quoteId} deleted`);
        } catch (err) {
            toast.error(errText(err));
        }
    };

    const syncCatalogue = async () => {
        if (!window.confirm("Sync the release catalogue (26 SKUs) into this site's database? Enquiries, quotes and drums are untouched.")) return;
        try {
            const res = await api.put("/admin/catalogue/sync", {}, { headers: authHeader() });
            toast.success(`Catalogue synced — ${res.data.total_products} products live`, { description: `${res.data.written} written/updated` });
        } catch (err) {
            toast.error(errText(err));
        }
    };

    const today = new Date().toISOString().slice(0, 10);
    const todayCount = enquiries.filter((e) => (e.created_at || "").slice(0, 10) === today).length;

    const addDrum = async (payload, done) => {
        try {
            await api.post("/drums", payload, { headers: authHeader() });
            toast.success("Drum added to register");
            load();
        } catch (err) {
            toast.error(errText(err));
        } finally {
            done();
        }
    };

    const updateDrum = async (drumId, update) => {
        try {
            await api.put(`/drums/${drumId}`, update, { headers: authHeader() });
            toast.success(`${drumId} updated`);
            load();
        } catch (err) {
            toast.error(errText(err));
        }
    };

    const deleteDrum = async (drumId) => {
        if (!window.confirm(`Delete drum ${drumId} from the register?`)) return;
        try {
            await api.delete(`/drums/${drumId}`, { headers: authHeader() });
            setDrums((p) => p.filter((d) => d.drum_id !== drumId));
            toast.success(`${drumId} removed`);
        } catch (err) {
            toast.error(errText(err));
        }
    };

    const approveCutting = async (jobId) => {
        if (!window.confirm(`Approve ${jobId} and deduct the planned metres from the drums?`)) return;
        setApproving(jobId);
        try {
            const res = await api.post(`/cutting/jobs/${jobId}/approve`, {}, { headers: authHeader() });
            toast.success(`${jobId} approved`, { description: res.data.deducted.map((d) => `${d.meters} m from ${d.drum_id}`).join(" · ") });
            load();
        } catch (err) {
            toast.error(errText(err));
        } finally {
            setApproving(null);
        }
    };

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="admin-page">
            <Seo title="Admin" noindex path="/admin" />
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-paper/85 border-b border-line">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo size={28} />
                        <span className="font-display font-bold tracking-[0.06em] text-lg text-ink hidden sm:block">
                            Roxy Cables<span className="text-copper text-[9px] align-super ml-0.5">TM</span>
                        </span>
                        <span className="font-mono text-[10px] tracking-[0.25em] text-copper uppercase ml-2">
                            Admin Desk
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <ThemeToggle />
                        {authed && (
                            <>
                                <button data-testid="admin-sync-catalogue" onClick={syncCatalogue} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-copper transition-colors duration-300" title="Push the release catalogue into this database">
                                    <Package size={14} /> Sync Catalogue
                                </button>
                                <button data-testid="admin-refresh-button" onClick={load} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-copper transition-colors duration-300">
                                    <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
                                </button>
                                <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-copper transition-colors duration-300">
                                    <LogOut size={13} /> Logout
                                </button>
                            </>
                        )}
                        <Link to="/" data-testid="admin-back-link" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-copper transition-colors duration-300">
                            <ArrowLeft size={14} /> Site
                        </Link>
                    </div>
                </div>
            </header>

            {!authed ? (
                <div className="max-w-md mx-auto px-4 sm:px-8 py-20">
                    <Eyebrow className="mb-4">Restricted — Staff Only</Eyebrow>
                    <h1 className="font-display font-bold tracking-tight leading-[1.05] text-4xl text-ink mb-3">
                        Admin <em className="text-copper">sign-in.</em>
                    </h1>
                    <p className="text-mist text-sm mb-8">
                        This desk holds customer enquiries and quotations. Authorised Roxy Cables staff only.
                    </p>
                    <form onSubmit={login} className="rounded-md border border-line bg-white p-6 space-y-4" data-testid="admin-login-form">
                        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                            <Lock size={12} className="text-copper" /> Password protected
                        </div>
                        <input data-testid="admin-login-email" type="email" required autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Admin email" className={inputCls} />
                        <input data-testid="admin-login-password" type="password" required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className={inputCls} />
                        <button data-testid="admin-login-submit" disabled={signingIn} className="w-full rounded-full bg-ink text-white font-mono uppercase tracking-[0.18em] text-[11px] py-4 hover:bg-copper transition-colors duration-300 disabled:opacity-50">
                            {signingIn ? "Signing in…" : "Sign In"}
                        </button>
                    </form>
                </div>
            ) : (
                <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-14">
                    <Eyebrow className="mb-4">Internal — Roxy Cables</Eyebrow>
                    <h1 className="font-display font-bold tracking-tight leading-[1.05] text-4xl md:text-5xl text-ink mb-8">
                        What needs to <em className="text-copper">flow.</em>
                    </h1>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mb-10">
                        <div className="border border-line bg-white rounded-md p-5 shadow-sm">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">Enquiries</p>
                            <p className="font-display font-bold text-4xl text-ink" data-testid="admin-total-count">{loading ? "—" : enquiries.length}</p>
                        </div>
                        <div className="border border-line bg-white rounded-md p-5 shadow-sm">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">Today</p>
                            <p className="font-display font-bold text-4xl text-copper" data-testid="admin-today-count">{loading ? "—" : todayCount}</p>
                        </div>
                        <div className="border border-line bg-white rounded-md p-5 shadow-sm">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">Quotations</p>
                            <p className="font-display font-bold text-4xl text-ink" data-testid="admin-quotes-count">{loading ? "—" : quotes.length}</p>
                        </div>
                        <div className="border border-line bg-white rounded-md p-5 shadow-sm">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">Follow-ups due</p>
                            <p className="font-display font-bold text-4xl text-copper" data-testid="admin-followups-count">
                                {loading ? "—" : enquiries.filter((e) => e.follow_up_at && e.follow_up_at <= today && !["won", "lost"].includes(e.stage || "new")).length}
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-2 mb-8" data-testid="admin-tabs">
                        {[["dashboard", "Dashboard"], ["enquiries", "Enquiries"], ["quotes", "Quotations"], ["prices", "Price List"], ["estimates", "Estimates"], ["inventory", "Inventory"], ["cutting", "Cutting"], ["site", "Site Content"]].map(([k, label]) => (
                            <button
                                key={k}
                                data-testid={`admin-tab-${k}`}
                                onClick={() => setTab(k)}
                                className={`font-mono text-[11px] uppercase tracking-[0.18em] px-5 py-2.5 border rounded-full transition-colors duration-300 ${
                                    tab === k ? "bg-ink border-ink text-white" : "border-line text-mist hover:border-copper hover:text-copper"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {tab === "dashboard" ? (
                        <DashboardTab />
                    ) : tab === "cutting" ? (
                        <CuttingTab jobs={cuttingJobs} onApprove={approveCutting} approving={approving} />
                    ) : tab === "prices" ? (
                        <PriceManagerTab />
                    ) : tab === "estimates" ? (
                        <EstimatesTab />
                    ) : tab === "site" ? (
                        <SiteContentTab />
                    ) : tab === "inventory" ? (
                        <InventoryTab drums={drums} products={products} onAdd={addDrum} onUpdate={updateDrum} onDelete={deleteDrum} />
                    ) : tab === "quotes" ? (
                        <QuotesTab quotes={quotes} onStatus={setStatus} onPdf={downloadPdf} onEmail={emailQuote} onDelete={deleteQuote} emailing={emailing} />
                    ) : loading ? (
                        <div className="space-y-3">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="border border-line bg-white rounded-md h-28 animate-pulse" />
                            ))}
                        </div>
                    ) : enquiries.length === 0 ? (
                        <div className="border border-line bg-white rounded-md p-12 text-center" data-testid="admin-empty-state">
                            <Inbox size={28} className="text-copper mx-auto mb-4" />
                            <p className="font-display font-semibold text-2xl text-ink mb-2">No enquiries yet</p>
                            <p className="text-mist text-sm">Send a test one from the site's enquiry form — it lands here instantly.</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2 mb-5" data-testid="stage-filters">
                                {["all", ...STAGES].map((s) => {
                                    const count = s === "all" ? enquiries.length : enquiries.filter((x) => (x.stage || "new") === s).length;
                                    return (
                                        <button
                                            key={s}
                                            data-testid={`stage-filter-${s}`}
                                            onClick={() => setStageFilter(s)}
                                            className={`font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-1.5 border rounded-full transition-colors duration-300 ${
                                                stageFilter === s ? "bg-ink border-ink text-white" : "border-line text-mist hover:border-copper hover:text-copper"
                                            }`}
                                        >
                                            {s === "all" ? "All" : STAGE_LABEL[s]} · {count}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="border border-line bg-white rounded-md divide-y divide-line overflow-hidden" data-testid="admin-enquiry-list">
                                {enquiries
                                    .filter((e) => stageFilter === "all" || (e.stage || "new") === stageFilter)
                                    .map((e) => (
                                        <EnquiryCard key={e.id} e={e} onQuote={setQuoteFor} onDelete={deleteEnquiry} onStage={setStage} onNote={addNote} onFollowUp={saveFollowUp} />
                                    ))}
                            </div>
                        </>
                    )}

                    <p className="mt-10 font-mono text-[10px] tracking-[0.2em] uppercase text-dim">
                        Internal page — password protected. Not linked from the site.
                    </p>
                </div>
            )}

            {quoteFor !== null && (
                <QuoteBuilder enquiry={quoteFor} onClose={() => setQuoteFor(null)} onSaved={load} />
            )}
        </div>
    );
}
